import React from 'react';
import MaterialButton from '@mui/material/Button';
import {styled} from '@mui/material/styles';
import type {ButtonProps as MaterialButtonProps} from '@mui/material/Button';
import {excludeComponentProps} from '../utils/excludeComponentProps';

type FuelVariant = 'primary' | 'secondary' | 'text' | 'text-inline';

export interface ButtonProps extends Omit<MaterialButtonProps,
'variant' |
'color' |
'disableElevation' |
'disableFocusRipple' |
'centerRipple' |
'disableRipple' |
'disableTouchRipple' |
'focusRipple' |
'TouchRippleProps'
> {
    /* Fuel specific variations */
    variant?: FuelVariant;
    /**
     * Used to replace root node with different
     * component. For example, you can leverage
     * React Router's Link by passing it here.
     */
    component?: React.ElementType<any>;
}

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        primary: true;
        secondary: true;
        text: true;
        'text-inline': true;
    }
}

const StyledButton = styled(MaterialButton, {
    ...excludeComponentProps('subNavigationItems')
})<ButtonProps>(({
    theme, variant
}) => ({
    transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    minWidth: 'auto',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',

    ...variant === 'primary' && {
        backgroundColor: theme.tokens.FuelColorBackgroundActionPrimaryBase,
        color: theme.tokens.FuelColorTextActionPrimary,
        boxShadow: `${theme.tokens.FuelShadowXs}, ${theme.tokens.FuelShadowInsetButton}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`,
        '&:hover': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionPrimaryHovered,
            boxShadow: `${theme.tokens.FuelShadowXs}, ${theme.tokens.FuelShadowInsetButton}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`
        },
        '&:active': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionPrimaryPressed,
            boxShadow: `${theme.tokens.FuelShadowXs}, ${theme.tokens.FuelShadowInsetButton}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`
        }
    },
    ...variant === 'secondary' && {
        backgroundColor: theme.tokens.FuelColorBackgroundActionSecondaryBase,
        color: theme.tokens.FuelColorTextActionSecondary,
        boxShadow: `${theme.tokens.FuelShadowXs}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`,
        border: `1px solid ${theme.tokens.FuelColorBorderDark}`,
        borderTopColor: '#bfbfbf',
        '&:hover': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionSecondaryHovered,
            borderColor: theme.tokens.FuelColorBorderDark,
            boxShadow: `${theme.tokens.FuelShadowXs}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`
        },

        '&:active': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionSecondaryPressed,
            borderColor: theme.tokens.FuelColorBorderDark,
            boxShadow: `${theme.tokens.FuelShadowXs}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`
        }
    },
    ...variant === 'text' && {
        backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase,
        color: theme.tokens.FuelColorTextActionTertiary,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryHovered,
            boxShadow: 'none'
        },

        '&:active': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryPressed,
            boxShadow: 'none'
        }
    },
    ...variant === 'text-inline' && {
        backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase,
        color: theme.tokens.FuelColorTextActionTertiary,
        textDecoration: 'underline',
        borderRadius: '0',
        boxShadow: 'none',
        fontWeight: 'normal',
        padding: '0',
        minWidth: 'auto',
        '&:hover': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase,
            color: theme.tokens.FuelColorTextLinkHovered,
            textDecoration: 'underline',
            boxShadow: 'none'
        },

        '&:active': {
            boxShadow: 'none'
        }
    },
    '&:focus': {
        outline: 'none'
    },
    '&.Mui-focusVisible': {
        boxShadow: theme.tokens.FuelShadowFocused,
        ...variant === 'primary' && {
            boxShadow: `${theme.tokens.FuelShadowInsetButton}, ${theme.tokens.FuelShadowFocused}`
        },
        ...variant === 'secondary' && {
            backgroundColor: theme.tokens.FuelColorBackgroundActionSecondaryBase
        },
        ...variant === 'text' && {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase
        },
        ...variant === 'text-inline' && {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase,
            color: theme.tokens.FuelColorTextActionTertiary
        }
    },
    '&.MuiButton-sizeSmall': {
        height: '2rem',
        padding: '3px 9px',
        ...variant === 'primary' && {
            padding: '4px 10px'
        },
        ...variant === 'text' && {
            padding: '4px 5px'
        },
        ...variant === 'text-inline' && {
            padding: 0
        }
    },
    '&.MuiButton-sizeMedium': {
        height: '2.25rem',
        padding: '5px 15px',
        ...variant === 'primary' && {
            padding: '6px 16px'
        },
        ...variant === 'text' && {
            padding: '6px 8px'
        },
        ...variant === 'text-inline' && {
            padding: 0
        }
    },
    '&.MuiButton-sizeLarge': {
        height: '2.5rem',
        padding: '7px 22px',
        fontSize: theme.tokens.FuelFontSizeBase,

        ...variant === 'primary' && {
            padding: '8px 22px'
        },
        ...variant === 'text' && {
            padding: '8px 11px'
        },
        ...variant === 'text-inline' && {
            padding: 0
        }
    },
    '& .MuiButton-startIcon': {
        marginRight: theme.tokens.FuelSpacing1,
        ...variant === 'text-inline' && {
            marginRight: 0
        }
    },
    '& .MuiButton-endIcon': {
        marginLeft: theme.tokens.FuelSpacing1,
        ...variant === 'text-inline' && {
            marginLeft: 0
        }
    },
    '&[disabled], &.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
        ...variant === 'primary' && {
            backgroundColor: `${theme.tokens.FuelColorBackgroundActionPrimaryBase}`,
            color: theme.tokens.FuelColorTextActionPrimary,
            boxShadow: 'none',
            opacity: '.5'
        },
        ...variant === 'secondary' && {
            backgroundColor: theme.tokens.FuelColorBackgroundActionSecondaryBase,
            color: theme.tokens.FuelColorTextActionSecondary,
            borderColor: theme.tokens.FuelColorBorderLight,
            boxShadow: 'none',
            opacity: '.5'
        },
        ...variant === 'text' && {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase,
            color: theme.tokens.FuelColorTextActionTertiary,
            opacity: '.5'
        },
        ...variant === 'text-inline' && {
            color: theme.tokens.FuelColorTextActionTertiary,
            opacity: '.5'
        }
    }
}));

export const Button = React.forwardRef<any, ButtonProps>(
    (
        {
            variant = 'secondary',
            size = 'medium',
            children,
            ...props
        },
        ref
    ) => {
        return (
            <StyledButton
                disableElevation={true}
                disableRipple={true}
                ref={ref}
                size={size}
                variant={variant}
                {...props}
            >{children}</StyledButton>
        );
    });

Button.displayName = 'Button';
